// src/components/ReferralAudit.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Input,
  Select,
  Flex,
  Button,
} from "@chakra-ui/react";
import { supabase } from "../utils/supabaseClient";
import useBusinessGuard from "../hooks/useBusinessGuard";

/**
 * ReferralAudit
 * Displays referral audit log for the current business.
 * ✅ Uses UUID identifiers (business_uuid).
 */
export default function ReferralAudit() {
  const { businessIdUuid, loading: authLoading } = useBusinessGuard(); // ✅ use UUID
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEmail, setFilterEmail] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterDate, setFilterDate] = useState("");

  useEffect(() => {
    if (authLoading || !businessIdUuid) return;

    const fetchAudit = async () => {
      try {
        const { data, error } = await supabase
          .from("referral_audit_log")
          .select("id, referrer_email, invitee_email, action, created_at, status")
          .eq("business_id", businessIdUuid) // ✅ UUID filter
          .order("created_at", { ascending: false });

        if (error) throw error;
        setAudit(data || []);
      } catch (err) {
        console.error("[ReferralAudit] Failed to fetch audit log:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAudit();
  }, [authLoading, businessIdUuid]);

  const filteredAudit = audit.filter((entry) => {
    return (
      (!filterEmail || (entry.referrer_email || "").includes(filterEmail)) &&
      (!filterAction || entry.action === filterAction) &&
      (!filterDate || entry.created_at.startsWith(filterDate))
    );
  });

  const exportCSV = (data) => {
    const headers = ["Referrer", "Invitee", "Action", "Date", "Status"];
    const rows = data.map((entry) => [
      entry.referrer_email,
      entry.invitee_email,
      entry.action,
      new Date(entry.created_at).toLocaleDateString(),
      entry.status,
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "referral_audit.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (authLoading || !businessIdUuid) return null; // ✅ Prevent premature render

  return (
    <Box p={4} bg="gray.50" borderRadius="md">
      <Text fontWeight="bold" mb={2}>
        🔍 Referral Audit Log
      </Text>

      <Flex gap={4} mb={4}>
        <Input
          placeholder="Referrer email"
          value={filterEmail}
          onChange={(e) => setFilterEmail(e.target.value)}
        />
        <Select
          placeholder="Action"
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
        >
          <option value="signup">Signup</option>
          <option value="booking">Booking</option>
        </Select>
        <Input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
        />
        <Button size="sm" onClick={() => exportCSV(filteredAudit)}>
          Export CSV
        </Button>
      </Flex>

      {loading ? (
        <Spinner />
      ) : filteredAudit.length === 0 ? (
        <Text>No referral activity found.</Text>
      ) : (
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Referrer</Th>
              <Th>Invitee</Th>
              <Th>Action</Th>
              <Th>Date</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredAudit.map((entry) => (
              <Tr key={entry.id}>
                <Td>{entry.referrer_email}</Td>
                <Td>{entry.invitee_email}</Td>
                <Td>{entry.action}</Td>
                <Td>{new Date(entry.created_at).toLocaleDateString()}</Td>
                <Td>
                  <Badge
                    colorScheme={entry.status === "credited" ? "green" : "gray"}
                  >
                    {entry.status}
                  </Badge>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Box>
  );
}
